import { leadStatusLabel } from '../utils/leadStatus';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import './Dashboard.css';
import { FiUsers, FiTrendingUp, FiClock, FiXCircle, FiActivity, FiArrowLeft, FiPhone, FiMail, FiEdit2, FiCheck, FiTrash2, FiAlertTriangle, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import SnehaDashboard from './SnehaDashboard';
import KripaDashboard from './KripaDashboard';

/**
 * Stage-1 processing Sneha (sneha@toniosenora.com / Sneha Rigin) — NOT Sneha Unnikrishnan (HR).
 * Broad name matching (e.g. includes "Sneha") wrongly treated HR as processing and hid her assigned leads.
 */
function isProcessingTeamSnehaUser(u) {
  if (!u) return false;
  if (u.role === 'HR') return false;
  const email = (u.email || '').toLowerCase();
  if (email === 'hr@toniosenora.com') return false;
  if (email === 'sneha@toniosenora.com') return true;
  const n = String(u.name || '').trim();
  return (
    n === 'Sneha' ||
    n === 'SNEHA' ||
    /^Sneha\s+Rigin$/i.test(n) ||
    /^SNEHA\s+RIGIN$/i.test(n)
  );
}

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { staffId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [phoneSearchInput, setPhoneSearchInput] = useState('');
  const isAutoRefreshRef = useRef(false);

  // Drill-down state for "ASSIGNED BY ME"
  const [isAssignedLeadsModalOpen, setIsAssignedLeadsModalOpen] = useState(false);
  const [selectedStaffName, setSelectedStaffName] = useState('');
  const [selectedStaffLeads, setSelectedStaffLeads] = useState([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  /** Staff personal dash: unattended list hidden until expanded */
  const [unattendedListExpanded, setUnattendedListExpanded] = useState(false);

  // Custom dashboard mode for dual-access users
  const [dashboardMode, setDashboardMode] = useState('processing');


  const fetchAssignedLeads = async (staffId, staffName) => {
    setIsLoadingLeads(true);
    setSelectedStaffName(staffName);
    setIsAssignedLeadsModalOpen(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/dashboard/assigned-leads/${staffId}`);
      setSelectedStaffLeads(response.data);
    } catch (error) {
      console.error('Error fetching assigned leads:', error);
    } finally {
      setIsLoadingLeads(false);
    }
  };

  // Processing Sneha only (excludes HR Sneha Unnikrishnan)
  const isSneha = isProcessingTeamSnehaUser(user);

  // Check if user is Kripa
  const isKripa = user?.name?.includes('Kripa') || user?.name?.includes('KRIPA') || user?.email === 'kripa@toniosenora.com';

  // Assignable admin who also works leads (matches server ASSIGNABLE_LEAD_ADMIN_EMAILS)
  const isSreelakshmi = (user?.email || '').toLowerCase() === 'sreelakshmi@toniosenora.com';

  // Check if user is Emy
  const isEmy = user?.name === 'Emy' || user?.name === 'EMY' || user?.email === 'emy@toniosenora.com';

  // Define fetchDashboardData BEFORE useEffect hooks that use it
  const fetchDashboardData = useCallback(async () => {
    const currentStaffId = staffId; // Capture current staffId
    const isAutoRefresh = isAutoRefreshRef.current;

    try {
      const endpoint = currentStaffId ? `${API_BASE_URL}/api/dashboard/staff/${currentStaffId}` : `${API_BASE_URL}/api/dashboard`;
      console.log('🔍 Fetching dashboard data:', { currentStaffId, endpoint, isAutoRefresh });

      const response = await axios.get(endpoint, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: {
          type: 'main',
          view: dashboardMode === 'main' ? 'personal' : undefined,
          _t: new Date().getTime() // Add timestamp to prevent caching
        }
      });

      console.log('📥 Dashboard data received:', {
        staffId: currentStaffId || 'main',
        staffName: response.data.staff?.name,
        staffIdFromResponse: response.data.staff?.id,
        totalLeads: response.data.metrics?.totalLeads,
        totalClients: response.data.metrics?.totalClients,
        registrationCompleted: response.data.metrics?.leadsByStatus?.['Registration Completed'],
        isProcessingTeam: response.data.isProcessingTeam,
        role: response.data.role,
        staffPerformanceCount: response.data.staffPerformance?.length || 0,
        staffPerformance: response.data.staffPerformance,
        isAutoRefresh
      });
      // Detailed log for sales team head
      if (response.data.role === 'SALES_TEAM_HEAD') {
        console.log('🔍 SALES_TEAM_HEAD - Full staffPerformance data:', JSON.stringify(response.data.staffPerformance, null, 2));
        console.log('🔍 SALES_TEAM_HEAD - staffPerformance type:', typeof response.data.staffPerformance);
        console.log('🔍 SALES_TEAM_HEAD - staffPerformance isArray:', Array.isArray(response.data.staffPerformance));
        console.log('🔍 SALES_TEAM_HEAD - staffPerformance length:', response.data.staffPerformance?.length);
      }

      // Verify we got the correct staff data when viewing a specific staff
      if (currentStaffId && response.data.staff) {
        const receivedStaffId = Number(response.data.staff.id);
        const requestedStaffId = Number(currentStaffId);
        if (receivedStaffId !== requestedStaffId) {
          console.error('❌ CRITICAL: Staff ID mismatch!');
          console.error('  Requested staff ID:', requestedStaffId);
          console.error('  Received staff ID:', receivedStaffId);
          console.error('  Received staff name:', response.data.staff.name);
          // During auto-refresh, don't clear data - just log the error
          if (isAutoRefresh) {
            console.warn('⚠️ Auto-refresh: Staff ID mismatch - keeping current data');
            return;
          }
          // On initial load, clear data
          setData(null);
          setLoading(false);
          return;
        }
      }

      setData(response.data);
    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);

      // During auto-refresh, don't redirect or clear data on errors
      if (isAutoRefresh) {
        console.warn('⚠️ Auto-refresh error - keeping current data:', error.message);
        return;
      }

      // Only redirect/clear on initial load errors
      if (error.response?.status === 404) {
        console.error('Staff not found, redirecting to main dashboard...');
        navigate('/');
      } else if (error.response?.status === 403) {
        console.error('Access denied, redirecting...');
        navigate('/');
      }
      setData(null);
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  }, [staffId, navigate]);

  // Function to fetch ONLY metrics for auto-update
  const fetchMetricsOnly = useCallback(async () => {
    const currentStaffId = staffId;
    try {
      const endpoint = currentStaffId ? `${API_BASE_URL}/api/dashboard/staff/${currentStaffId}` : `${API_BASE_URL}/api/dashboard`;
      const response = await axios.get(endpoint, {
        params: {
          type: 'main',
          view: dashboardMode === 'main' ? 'personal' : undefined,
          metricsOnly: 'true',
          _t: new Date().getTime()
        },
        headers: { 'Cache-Control': 'no-cache' }
      });

      if (response.data.metrics) {
        setData(prevData => {
          if (!prevData) return prevData;
          return {
            ...prevData,
            metrics: response.data.metrics,
            ...(response.data.unattendedLeads !== undefined && {
              unattendedLeads: response.data.unattendedLeads,
              unattendedCount: response.data.unattendedCount,
            }),
          };
        });
        console.log('📈 Metrics auto-updated:', response.data.metrics);
      }
    } catch (error) {
      console.warn('⚠️ Metrics auto-update failed:', error.message);
    }
  }, [staffId]);

  // Handle Lead Deletion
  const handleDelete = async (leadId, leadName) => {
    if (window.confirm(`Are you sure you want to delete lead: "${leadName}"? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API_BASE_URL}/api/leads/${leadId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        // Refresh list
        fetchDashboardData();
      } catch (error) {
        console.error('Delete error:', error);
        toast.error('Failed to delete lead: ' + (error.response?.data?.error || 'You might not have permission to delete this lead.'));
      }
    }
  };

  const handleSelect = (id) => {
    setSelectedLeads(prev => {
      if (prev.includes(id)) {
        return prev.filter(leadId => leadId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = (data?.recentLeads || []).map(lead => lead.id);
      setSelectedLeads(allIds);
    } else {
      setSelectedLeads([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedLeads.length} selected lead(s)? This action cannot be undone.`)) {
      try {
        await Promise.all(selectedLeads.map(id =>
          axios.delete(`${API_BASE_URL}/api/leads/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
        ));
        setSelectedLeads([]);
        fetchDashboardData();
      } catch (error) {
        console.error('Bulk delete error:', error);
        toast.error('Failed to delete some leads. You might not have permission.');
        fetchDashboardData();
      }
    }
  };

  // Client Bulk Handlers
  const handleSelectClient = (id) => {
    setSelectedClients(prev => {
      if (prev.includes(id)) {
        return prev.filter(clientId => clientId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAllClients = (e) => {
    if (e.target.checked) {
      const allIds = (data?.recentClients || []).map(client => client.id);
      setSelectedClients(allIds);
    } else {
      setSelectedClients([]);
    }
  };

  const handleBulkDeleteClients = async () => {
    if (selectedClients.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedClients.length} selected client(s)? This action cannot be undone.`)) {
      try {
        await Promise.all(selectedClients.map(id =>
          axios.delete(`${API_BASE_URL}/api/clients/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
        ));
        setSelectedClients([]);
        fetchDashboardData();
      } catch (error) {
        console.error('Bulk delete error:', error);
        toast.error('Failed to delete some clients. You might not have permission.');
        fetchDashboardData();
      }
    }
  };

  const handleQuickPhoneSearch = (e) => {
    e.preventDefault();
    if (phoneSearchInput.trim()) {
      navigate(`/leads?phone=${phoneSearchInput.trim()}`);
    }
  };

  useEffect(() => {
    setUnattendedListExpanded(false);
  }, [staffId]);

  useEffect(() => {
    const canUseStaffDashboardRoute =
      user &&
      (user.role === 'ADMIN' ||
        user.role === 'HR' ||
        user.role === 'SALES_TEAM_HEAD' ||
        isEmy);
    if (staffId && user && !canUseStaffDashboardRoute) {
      navigate('/');
      return;
    }
    // For regular users, fetch always. For Sneha/Kripa, only fetch if in 'main' mode
    if ((!isSneha && !isKripa) || dashboardMode === 'main') {
      // Clear old data when staffId or mode changes 
      isAutoRefreshRef.current = false;
      setData(null);
      setLoading(true);
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [staffId, user, navigate, isEmy, isSneha, isKripa, dashboardMode, fetchDashboardData]);

  // Auto-refresh when page becomes visible (user switches tabs/windows)
  useEffect(() => {
    if (isSneha || isKripa) return; // Skip for specialized dashboards

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        console.log('🔄 Dashboard page visible, refreshing...', { staffId });
        isAutoRefreshRef.current = true;
        fetchDashboardData().finally(() => {
          isAutoRefreshRef.current = false;
        });
      }
    };

    const handleFocus = () => {
      if (user) {
        console.log('🔄 Window focused, refreshing dashboard...', { staffId });
        isAutoRefreshRef.current = true;
        fetchDashboardData().finally(() => {
          isAutoRefreshRef.current = false;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // ONLY auto-update metrics every 10 seconds
    const interval = setInterval(() => {
      if (user && !isSneha && !isKripa) {
        fetchMetricsOnly();
      }
    }, 10000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [user, isSneha, isKripa, staffId, fetchDashboardData, fetchMetricsOnly]);

  // Debug: Log metrics when data changes (must be before conditional returns)
  useEffect(() => {
    if (data && data.metrics) {
      console.log('📊 Dashboard metrics updated:', {
        totalLeads: data.metrics.totalLeads,
        totalClients: data.metrics.totalClients,
        registrationCompleted: data.metrics.leadsByStatus?.['Registration Completed'],
        allStatuses: data.metrics.leadsByStatus
      });
    }
    if (data) {
      console.log('📊 Dashboard data check:', {
        role: data.role,
        hasStaffPerformance: !!data.staffPerformance,
        staffPerformanceType: typeof data.staffPerformance,
        staffPerformanceIsArray: Array.isArray(data.staffPerformance),
        staffPerformanceLength: data.staffPerformance?.length || 0,
        staffPerformance: data.staffPerformance
      });
      // Detailed log for sales team head
      if (data.role === 'SALES_TEAM_HEAD') {
        console.log('🔍 SALES_TEAM_HEAD - Full staffPerformance in state:', JSON.stringify(data.staffPerformance, null, 2));
        console.log('🔍 SALES_TEAM_HEAD - Will render?', data.staffPerformance && Array.isArray(data.staffPerformance) && data.staffPerformance.length > 0);
      }
    }
  }, [data]);

  // Route to specialized dashboards (after all hooks)
  // Show Sneha's dashboard if:
  // 1. User is Sneha viewing their own dashboard (no staffId)
  // 2. Admin is viewing Sneha's dashboard (staffId matches Sneha)
  const viewingSneha = isSneha && !staffId;

  // Check if admin is viewing Sneha - use multiple methods to detect
  // Method 1: Check if data is loaded and contains Sneha info
  // Method 2: If data not loaded yet but staffId exists, fetch staff info to check
  // Method 3: Check processingRole from data
  const adminViewingSneha = staffId && (user?.role === 'ADMIN' || user?.role === 'SALES_TEAM_HEAD') && (
    // If data is loaded, check staff name/email or processingRole
    (data && (
      (data.staff && (data.staff.name === 'Sneha' || data.staff.name === 'SNEHA' || data.staff.email === 'sneha@toniosenora.com')) ||
      (data.processingRole === 'sneha')
    )) ||
    // If data not loaded yet, we'll check in the isStaffDetailView section below
    (!data && loading)
  );

  // Show Kripa's dashboard if:
  // 1. User is Kripa viewing their own dashboard (no staffId)
  // 2. Admin is viewing Kripa's dashboard (staffId matches Kripa)
  const viewingKripa = isKripa && !staffId;
  // Check if admin is viewing Kripa
  const adminViewingKripa = staffId && (user?.role === 'ADMIN' || user?.role === 'SALES_TEAM_HEAD') && (
    (data && (
      (data.staff && (data.staff.name === 'Kripa' || data.staff.name === 'KRIPA' || data.staff.email === 'kripa@toniosenora.com')) ||
      (data.processingRole === 'kripa')
    )) ||
    (!data && loading)
  );

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  // Dual Dashboard Switcher for Sneha/Kripa (Specialized Processing)
  if ((viewingSneha || viewingKripa) && dashboardMode === 'processing') {
    return (
      <div className="dashboard-container" style={{ padding: '0 20px' }}>
        <div style={{ padding: '20px 0', borderBottom: '1px solid #e5e7eb', marginBottom: '20px', display: 'flex', gap: '10px' }}>
          <button
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              backgroundColor: '#2563eb',
              color: 'white'
            }}
          >
            {viewingSneha ? 'Stage 1 Processing' : 'Stage 2 Processing'}
          </button>
          <button
            onClick={() => setDashboardMode('main')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: 600,
              border: '1px solid #d1d5db',
              cursor: 'pointer',
              backgroundColor: 'white',
              color: '#374151'
            }}
          >
            My Leads
          </button>
        </div>
        {viewingSneha ? <SnehaDashboard viewingStaffId={null} /> : <KripaDashboard viewingStaffId={null} />}
      </div>
    );
  }

  if (!data) {
    return <div className="dashboard-error">Error loading dashboard data</div>;
  }


  // After data is loaded, check again for admin viewing Sneha/Kripa
  // Sneha's ID is 12, Kripa's ID is 8 (from backend code)
  const snehaStaffId = 12;
  const kripaStaffId = 13;

  const adminViewingSnehaAfterLoad = staffId && (user?.role === 'ADMIN' || user?.role === 'SALES_TEAM_HEAD') && (
    Number(staffId) === snehaStaffId || // Direct ID check
    (data.staff && (data.staff.name === 'Sneha' || data.staff.name === 'SNEHA' || data.staff.email === 'sneha@toniosenora.com')) ||
    (data.processingRole === 'sneha')
  );

  const adminViewingKripaAfterLoad = staffId && (user?.role === 'ADMIN' || user?.role === 'SALES_TEAM_HEAD') && (
    Number(staffId) === kripaStaffId || // Direct ID check
    (data.staff && (data.staff.name === 'Kripa' || data.staff.name === 'KRIPA' || data.staff.email === 'kripa@toniosenora.com')) ||
    (data.processingRole === 'kripa')
  );

  if (adminViewingSnehaAfterLoad) {
    return (
      <div>
        <button className="dashboard-back" onClick={() => navigate('/')} style={{ marginBottom: '16px' }}>
          <FiArrowLeft /> Back to Dashboard
        </button>
        <SnehaDashboard viewingStaffId={Number(staffId)} />
      </div>
    );
  }

  if (adminViewingKripaAfterLoad) {
    return (
      <div>
        <button className="dashboard-back" onClick={() => navigate('/')} style={{ marginBottom: '16px' }}>
          <FiArrowLeft /> Back to Dashboard
        </button>
        <KripaDashboard viewingStaffId={Number(staffId)} />
      </div>
    );
  }

  const isStaffDetailView = Boolean(staffId);
  const isRestrictedRole =
    data.role === 'STAFF' ||
    data.role === 'SALES_TEAM' ||
    data.role === 'PROCESSING' ||
    data.role === 'HR';
  const isAdminOrTeamHead = data.role === 'ADMIN' || data.role === 'SALES_TEAM_HEAD';
  const isReadOnly = data.isReadOnly || false; // Emy's read-only access

  const renderStaffMetrics = () => {
    // Don't show lead metrics for processing team (they don't deal with leads)
    if (isStaffDetailView && data.isProcessingTeam) {
      return null;
    }

    return (
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon" style={{ background: '#FFF4D6' }}>
            <FiUsers style={{ color: '#D4AF37' }} />
          </div>
          <div className="metric-content">
            <div className="metric-value">{data.metrics?.totalLeads ?? 0}</div>
            <div className="metric-label">Total Leads</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon" style={{ background: '#FFF4D6' }}>
            <FiTrendingUp style={{ color: '#D4AF37' }} />
          </div>
          <div className="metric-content">
            <div className="metric-value">{data.metrics?.newLeads ?? 0}</div>
            <div className="metric-label">New Leads</div>
          </div>
        </div>
        <div
          className="metric-card metric-card-clickable"
          onClick={() => navigate('/leads?follow_up_date=today')}
          style={{ cursor: 'pointer' }}
          title="View leads with follow-up set for today"
        >
          <div className="metric-icon" style={{ background: '#FFF4D6' }}>
            <FiClock style={{ color: '#D4AF37' }} />
          </div>
          <div className="metric-content">
            <div className="metric-value">{data.metrics?.todayFollowups ?? 0}</div>
            <div className="metric-label">Today's Follow-ups</div>
          </div>
        </div>
        <div
          className="metric-card metric-card-clickable"
          onClick={() => navigate('/leads?follow_up_overdue=true')}
          style={{ cursor: 'pointer' }}
          title="View overdue follow-ups"
        >
          <div className="metric-icon" style={{ background: '#FFF4D6' }}>
            <FiXCircle style={{ color: '#D4AF37' }} />
          </div>
          <div className="metric-content">
            <div className="metric-value">{data.metrics?.dueFollowups ?? 0}</div>
            <div className="metric-label">Due Follow-ups</div>
          </div>
        </div>
      </div>
    );
  };

  /** Overdue follow-ups not marked completed/skipped — same logic as dashboard API. List is collapsed until staff expands it. */
  const renderUnattendedLeadsSection = () => {
    const list = data?.unattendedLeads;
    const count = data?.unattendedCount ?? list?.length ?? 0;
    if (!count || !list?.length) return null;
    if (isStaffDetailView && data.isProcessingTeam) return null;

    return (
      <div className="dashboard-unattended-section" style={{ marginBottom: '24px' }}>
        <button
          type="button"
          onClick={() => setUnattendedListExpanded((o) => !o)}
          aria-expanded={unattendedListExpanded}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            width: '100%',
            textAlign: 'left',
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(185, 28, 28, 0.08)',
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          {unattendedListExpanded ? (
            <FiChevronDown size={22} style={{ color: '#b91c1c', flexShrink: 0, marginTop: '4px' }} aria-hidden />
          ) : (
            <FiChevronRight size={22} style={{ color: '#b91c1c', flexShrink: 0, marginTop: '4px' }} aria-hidden />
          )}
          <FiAlertTriangle size={24} style={{ color: '#b91c1c', flexShrink: 0, marginTop: '2px' }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '17px', fontWeight: 700, color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              Unreachable follow-ups
              <span
                style={{
                  background: '#b91c1c',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '999px',
                }}
              >
                {count}
              </span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#7f1d1d', lineHeight: 1.45 }}>
              {unattendedListExpanded
                ? 'Past follow-up date — open a lead to update or complete the follow-up.'
                : 'Click to view the full list. These leads are past their follow-up date and need attention.'}
            </p>
          </div>
        </button>
        {unattendedListExpanded && (
          <div
            className="leads-list-table"
            style={{ marginTop: '12px', border: '1px solid #fecaca', borderRadius: '10px', overflow: 'hidden' }}
          >
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Follow-up</th>
                  <th>Days overdue</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((lead) => (
                  <tr key={lead.id}>
                    <td>
                      <button
                        type="button"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: '#1d4ed8',
                          fontWeight: 600,
                          cursor: 'pointer',
                          textAlign: 'left',
                          textDecoration: 'underline',
                        }}
                      >
                        {lead.name}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FiPhone style={{ fontSize: '14px', opacity: 0.6 }} />
                        {lead.phone_country_code ? `${lead.phone_country_code} ` : ''}
                        {lead.phone_number || '—'}
                      </div>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor: `${getStatusColor(lead.status)}20`,
                          color: getStatusColor(lead.status),
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        {leadStatusLabel(lead.status)}
                      </span>
                    </td>
                    <td>
                      {lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ fontWeight: 600, color: '#b91c1c' }}>{lead.days_overdue ?? '—'}</td>
                    <td>
                      <button className="btn-view-lead" type="button" onClick={() => navigate(`/leads/${lead.id}`)}>
                        <FiEdit2 /> Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const getStatusBoxColor = (status) => {
    const colors = {
      'New': {
        backgroundColor: '#BFDBFE', // Light blue
        color: '#1e3a8a', // Dark blue text
        borderColor: '#3b82f6' // Blue border
      },
      'Follow-up': {
        backgroundColor: '#FBCFE8', // Light pink
        color: '#831843', // Dark pink text
        borderColor: '#ec4899' // Pink border
      },
      'Follow-up 1': {
        backgroundColor: '#E6E6FA', // Light lavender
        color: '#4B0082', // Indigo text
        borderColor: '#8b5cf6'
      },
      'Follow-up 2': {
        backgroundColor: '#D8D4F7', // Slightly darker lavender
        color: '#4B0082',
        borderColor: '#7c3aed'
      },
      'Follow-up 3': {
        backgroundColor: '#CAC2F4', // Even darker lavender
        color: '#4B0082',
        borderColor: '#6d28d9'
      },
      'Prospect': {
        backgroundColor: '#A7F3D0', // Light green
        color: '#065f46', // Dark green text
        borderColor: '#10b981' // Green border
      },
      'Pending Lead': {
        backgroundColor: '#FDE68A', // Light yellow
        color: '#92400e', // Dark yellow/brown text
        borderColor: '#f59e0b' // Yellow border
      },
      'Not Responding': {
        backgroundColor: '#FEF3C7', // Light amber
        color: '#92400e', // Dark amber text
        borderColor: '#f59e0b' // Amber border
      },
      'Not Attended': {
        backgroundColor: '#FFEDD5',
        color: '#9a3412',
        borderColor: '#ea580c',
      },
      'Not Eligible': {
        backgroundColor: '#FEE2E2',
        color: '#991b1b',
        borderColor: '#ef4444',
      },
      'Wrong Number': {
        backgroundColor: '#E5E7EB', // Light gray
        color: '#4B5563', // Dark gray text
        borderColor: '#9CA3AF' // Gray border
      },
      'Manual Lead': {
        backgroundColor: '#FCD34D',
        color: '#92400e',
        borderColor: '#f59e0b',
      },
      'Not Interested': {
        backgroundColor: '#F3F4F6',
        color: '#4b5563',
        borderColor: '#9ca3af',
      },
      'Registration Completed': {
        backgroundColor: '#DBEAFE',
        color: '#1e40af',
        borderColor: '#3b82f6',
      },
      'Closed': {
        backgroundColor: '#FECACA', // Light red
        color: '#991B1B', // Dark red text
        borderColor: '#ef4444' // Red border
      },
      'Closed / Rejected': {
        backgroundColor: '#E5E7EB', // Light gray
        color: '#374151', // Dark gray text
        borderColor: '#6b7280' // Gray border
      },
    };
    return colors[status] || colors['New'];
  };

  const handleStatusClick = (status) => {
    // Navigate to leads page with status filter
    // We use URLSearchParams to ensure proper encoding
    const params = new URLSearchParams();
    
    // For Manual Lead, we want to show all leads not created via Bulk Import (any status)
    if (status === 'Manual Lead') {
      // 'direct' filter shows all leads not from bulk import (any status)
      params.set('lead_source_type', 'direct');
    } else {
      params.set('status', status);
    }
    
    navigate(`/leads?${params.toString()}`);
  };

  const renderStatusBreakdown = () => {
    // Don't show status breakdown for processing team (they don't deal with leads)
    if (isStaffDetailView && data.isProcessingTeam) {
      return null;
    }

    const newColor = getStatusBoxColor('New');
    const manualLeadColor = getStatusBoxColor('Manual Lead');
    const followUpColor = getStatusBoxColor('Follow-up');
    const prospectColor = getStatusBoxColor('Prospect');
    const pendingColor = getStatusBoxColor('Pending Lead');
    const notRespondingColor = getStatusBoxColor('Not Responding');
    const wrongNumberColor = getStatusBoxColor('Wrong Number');
    const closedColor = getStatusBoxColor('Closed');
    const registrationCompletedColor = getStatusBoxColor('Registration Completed');

    /** Always show these tiles with real counts from leadsByStatus */
    const STATUS_BOX_ORDER = [
      'New',
      'Manual Lead',
      'Follow-up',
      'Prospect',
      'Pending Lead',
      'Not Responding',
      'Wrong Number',
      'Not Attended',
      'Not Eligible',
      'Not Interested',
      'Closed',
      'Registration Completed',
    ];

    const lbs = data.metrics?.leadsByStatus;

    return (
      <div className="status-breakdown">
        <h2>Leads by Status</h2>
        <div className="status-grid">
          {lbs && typeof lbs === 'object' ? (
            STATUS_BOX_ORDER.map((status) => {
              const color = getStatusBoxColor(status);
              // For Follow-up, sum up all follow-up statuses (1, 2, 3)
              let count = lbs[status] ?? 0;
              if (status === 'Manual Lead') {
                // Match staff cards & filter: non–bulk (direct) leads, not only status "Manual Lead"
                count = Number(data.metrics?.manualLeads ?? lbs[status] ?? 0);
              }
              if (status === 'Follow-up') {
                count = (lbs['Follow-up 1'] || 0) + (lbs['Follow-up 2'] || 0) + (lbs['Follow-up 3'] || 0) + (lbs['Follow-up'] || 0);
              }
              return (
                <div
                  key={status}
                  className="status-item"
                  style={color}
                  onClick={() => handleStatusClick(status)}
                  title={
                    status === 'Manual Lead'
                      ? 'View leads entered manually or not from bulk import (direct)'
                      : `View ${leadStatusLabel(status)} leads`
                  }
                >
                  <span className="status-label" style={{ color: color.color }}>{leadStatusLabel(status)}</span>
                  <span className="status-count" style={{ color: color.color }}>{count}</span>
                </div>
              );
            })
          ) : (
            // Legacy metrics without leadsByStatus object - use real counts
            <>
              <div
                className="status-item"
                style={newColor}
                onClick={() => handleStatusClick('New')}
                title="View New Leads"
              >
                <span className="status-label" style={{ color: newColor.color }}>New</span>
                <span className="status-count" style={{ color: newColor.color }}>{data.metrics.newLeads || 0}</span>
              </div>
              <div
                className="status-item"
                style={manualLeadColor}
                onClick={() => handleStatusClick('Manual Lead')}
                title="View Manual Leads"
              >
                <span className="status-label" style={{ color: manualLeadColor.color }}>Manual Lead</span>
                <span className="status-count" style={{ color: manualLeadColor.color }}>{data.metrics.manualLeads || 0}</span>
              </div>
              <div
                className="status-item"
                style={followUpColor}
                onClick={() => handleStatusClick('Follow-up')}
                title="View All Follow-up Leads"
              >
                <span className="status-label" style={{ color: followUpColor.color }}>Follow-up</span>
                <span className="status-count" style={{ color: followUpColor.color }}>{data.metrics.followupLeads || 0}</span>
              </div>
              <div
                className="status-item"
                style={prospectColor}
                onClick={() => handleStatusClick('Prospect')}
                title="View Prospect Leads"
              >
                <span className="status-label" style={{ color: prospectColor.color }}>Prospect</span>
                <span className="status-count" style={{ color: prospectColor.color }}>{data.metrics.processingLeads || 0}</span>
              </div>
              <div
                className="status-item"
                style={pendingColor}
                onClick={() => handleStatusClick('Pending Lead')}
                title="View Pending Leads"
              >
                <span className="status-label" style={{ color: pendingColor.color }}>Pending Lead</span>
                <span className="status-count" style={{ color: pendingColor.color }}>{data.metrics.convertedLeads || 0}</span>
              </div>
              <div
                className="status-item"
                style={notRespondingColor}
                onClick={() => handleStatusClick('Not Responding')}
                title="View Not Reachable Leads"
              >
                <span className="status-label" style={{ color: notRespondingColor.color }}>Not Reachable</span>
                <span className="status-count" style={{ color: notRespondingColor.color }}>{data.metrics.notRespondingLeads || 0}</span>
              </div>
              <div
                className="status-item"
                style={wrongNumberColor}
                onClick={() => handleStatusClick('Wrong Number')}
                title="View Wrong Number Leads"
              >
                <span className="status-label" style={{ color: wrongNumberColor.color }}>Wrong Number</span>
                <span className="status-count" style={{ color: wrongNumberColor.color }}>{data.metrics.wrongNumberLeads || 0}</span>
              </div>
              <div
                className="status-item"
                style={closedColor}
                onClick={() => handleStatusClick('Closed')}
                title="View Closed Leads"
              >
                <span className="status-label" style={{ color: closedColor.color }}>Closed</span>
                <span className="status-count" style={{ color: closedColor.color }}>{data.metrics.closedLeads || 0}</span>
              </div>
              <div
                className="status-item"
                style={registrationCompletedColor}
                onClick={() => handleStatusClick('Registration Completed')}
                title="View Completed Registrations"
              >
                <span className="status-label" style={{ color: registrationCompletedColor.color }}>Registration Completed</span>
                <span className="status-count" style={{ color: registrationCompletedColor.color }}>{data.metrics.registrationCompletedLeads || 0}</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderAssignedByMe = () => {
    if (!data.assignedByMe || data.assignedByMe.length === 0) {
      return null;
    }

    return (
      <div className="dashboard-section assigned-by-me-section" style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>ASSIGNED BY ME</h2>
          <span style={{
            background: '#FFF4D6',
            color: '#D4AF37',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            boxShadow: '0 2px 4px rgba(212, 175, 55, 0.1)'
          }}>
            Total Leads Dispatched
          </span>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: '16px'
        }}>
          {data.assignedByMe.map((item) => (
            <div
              key={item.staff_id}
              onClick={() => fetchAssignedLeads(item.staff_id, item.staff_name)}
              style={{
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              className="assigned-staff-card"
            >
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{item.staff_name}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Assigned Staff</div>
              </div>
              <div style={{
                background: 'rgba(212, 175, 55, 0.1)',
                color: '#8B6914',
                padding: '8px 12px',
                borderRadius: '8px',
                fontWeight: '700',
                fontSize: '14px',
                textAlign: 'center',
                minWidth: '60px'
              }}>
                {item.assigned_count || 0}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAssignedLeadsModal = () => {
    if (!isAssignedLeadsModalOpen) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        backdropFilter: 'blur(4px)'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '1000px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          animation: 'modalFadeIn 0.3s ease-out'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#f9fafb'
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>
                Leads assigned to {selectedStaffName}
              </h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                Listing all leads dispatched by you to this staff member
              </p>
            </div>
            <button
              onClick={() => setIsAssignedLeadsModalOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9ca3af',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <FiXCircle size={24} />
            </button>
          </div>

          <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
            {isLoadingLeads ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px' }}>
                <div className="loading-spinner" style={{ marginBottom: '16px' }}></div>
                <p style={{ color: '#6b7280' }}>Fetching leads...</p>
              </div>
            ) : selectedStaffLeads.length > 0 ? (
              <div className="leads-list-table">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStaffLeads.map((lead, index) => (
                      <tr key={lead.id}>
                        <td style={{ color: '#6b7280', fontSize: '13px' }}>
                          {index + 1}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: '#111827' }}>{lead.name}</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FiPhone style={{ fontSize: '14px', color: '#6b7280' }} />
                            <span>{lead.phone_country_code} {lead.phone_number}</span>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FiMail style={{ fontSize: '14px', color: '#6b7280' }} />
                            <span>{lead.email || 'N/A'}</span>
                          </div>
                        </td>
                        <td>
                          <span className="status_badge" style={{
                            backgroundColor: `${getStatusColor(lead.status)}15`,
                            color: getStatusColor(lead.status),
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 600,
                            border: `1px solid ${getStatusColor(lead.status)}30`
                          }}>
                            {leadStatusLabel(lead.status)}
                          </span>
                        </td>
                        <td>
                          {lead.priority ? (
                            <span style={{
                              backgroundColor: `${getPriorityColor(lead.priority)}15`,
                              color: getPriorityColor(lead.priority),
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 600
                            }}>
                              {formatPriority(lead.priority)}
                            </span>
                          ) : '-'}
                        </td>
                        <td>
                          <button
                            className="btn-view-lead"
                            onClick={() => {
                              setIsAssignedLeadsModalOpen(false);
                              navigate(`/leads/${lead.id}`);
                            }}
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                          >
                            <FiEdit2 /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                <FiUsers size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                <p>No leads found for this staff member.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isRestrictedRole && !isStaffDetailView) {
    return (
      <div className="dashboard">
        {isKripa && (
          <div style={{ padding: '0 0 20px 0', borderBottom: '1px solid #e5e7eb', marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setDashboardMode('processing')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                fontWeight: 600,
                border: dashboardMode === 'processing' ? 'none' : '1px solid #d1d5db',
                cursor: 'pointer',
                backgroundColor: dashboardMode === 'processing' ? '#2563eb' : 'white',
                color: dashboardMode === 'processing' ? 'white' : '#374151'
              }}
            >
              Stage 2 Processing
            </button>
            <button
              onClick={() => setDashboardMode('main')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                fontWeight: 600,
                border: dashboardMode === 'main' ? 'none' : '1px solid #d1d5db',
                cursor: 'pointer',
                backgroundColor: dashboardMode === 'main' ? '#2563eb' : 'white',
                color: dashboardMode === 'main' ? 'white' : '#374151'
              }}
            >
              My Leads
            </button>
          </div>
        )}

        {isKripa && dashboardMode === 'processing' ? (
          <KripaDashboard />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h1 className="dashboard-title">
                {isKripa && dashboardMode === 'main' ? 'My Leads Dashboard' : 'My Dashboard'}
              </h1>
              {/* Hide search section in personal mode for cleaner UI */}
              {dashboardMode !== 'main' && (
                <form onSubmit={handleQuickPhoneSearch} className="dashboard-quick-search" style={{ marginTop: 0 }}>
                  <FiPhone className="search-icon" />
                  <input
                    type="text"
                    placeholder="Quick search by phone..."
                    value={phoneSearchInput}
                    onChange={(e) => setPhoneSearchInput(e.target.value)}
                  />
                </form>
              )}
            </div>
            {renderStaffMetrics()}
            {renderUnattendedLeadsSection()}
            {renderStatusBreakdown()}
            {dashboardMode !== 'main' && renderAssignedByMe()}
            <div className="recent-activity">
              <h2>Recent Activity</h2>
              {/* Activity list here */}
            </div>
            {renderAssignedLeadsModal()}
          </>
        )}
      </div>
    );
  }

  // Admin/Head Dashboard View
  if (!isStaffDetailView && (data.role === 'ADMIN' || data.role === 'SALES_TEAM_HEAD')) {
    return (
      <div className="dashboard">
        {isSneha && (
          <div style={{ padding: '0 0 20px 0', borderBottom: '1px solid #e5e7eb', marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setDashboardMode('processing')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                fontWeight: 600,
                border: dashboardMode === 'processing' ? 'none' : '1px solid #d1d5db',
                cursor: 'pointer',
                backgroundColor: dashboardMode === 'processing' ? '#2563eb' : 'white',
                color: dashboardMode === 'processing' ? 'white' : '#374151'
              }}
            >
              Stage 1 Processing
            </button>
            <button
              onClick={() => setDashboardMode('main')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                fontWeight: 600,
                border: dashboardMode === 'main' ? 'none' : '1px solid #d1d5db',
                cursor: 'pointer',
                backgroundColor: dashboardMode === 'main' ? '#2563eb' : 'white',
                color: dashboardMode === 'main' ? 'white' : '#374151'
              }}
            >
              My Leads
            </button>
          </div>
        )}

        {isSneha && dashboardMode === 'processing' ? (
          <SnehaDashboard />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h1 className="dashboard-title">
                  {data.role === 'ADMIN' ? (dashboardMode === 'main' ? 'My Leads Dashboard' : 'Company Dashboard') : 'Team Dashboard'}
                </h1>
              </div>
              {/* Hide search section in personal mode for cleaner UI */}
              {dashboardMode !== 'main' && (
                <form onSubmit={handleQuickPhoneSearch} className="dashboard-quick-search" style={{ marginTop: 0 }}>
                  <FiPhone className="search-icon" />
                  <input
                    type="text"
                    placeholder="Quick search by phone..."
                    value={phoneSearchInput}
                    onChange={(e) => setPhoneSearchInput(e.target.value)}
                  />
                </form>
              )}
            </div>
          </>
        )}

        {renderStaffMetrics()}
        {renderStatusBreakdown()}
        {dashboardMode !== 'main' && renderAssignedByMe()}
        {
          (data.staffPerformance && data.staffPerformance.length > 0 && dashboardMode !== 'main') && (
            <div className="staff-performance-section" style={{ marginBottom: '32px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                {data.role === 'SALES_TEAM_HEAD' ? 'My Team Performance' : 'Staff Performance Overview'}
              </h2>
              <div className="staff-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                {data.staffPerformance
                  .filter(staff => Number(staff.id) !== Number(user?.id) && staff.email !== user?.email)
                  .map((staff) => (
                    <div
                      key={staff.id}
                      className="staff-card"
                      onClick={() => navigate(`/dashboard/staff/${staff.id}`)}
                      style={{
                        cursor: 'pointer',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '20px',
                        backgroundColor: 'white',
                        transition: 'all 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#e0e7ff',
                          color: '#4f46e5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600',
                          fontSize: '16px',
                          marginRight: '12px'
                        }}>
                          {staff.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>{staff.name}</h3>
                          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>{staff.email}</p>
                        </div>
                        <div style={{ marginLeft: 'auto', color: '#9ca3af' }}>👉</div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                        <div style={{ backgroundColor: '#f9fafb', padding: '8px', borderRadius: '6px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Total</div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>{staff.total_leads || 0}</div>
                        </div>
                        <div style={{ backgroundColor: '#eff6ff', padding: '8px', borderRadius: '6px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', color: '#2563eb', textTransform: 'uppercase', marginBottom: '2px' }}>New</div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e40af' }}>{staff.new_leads || 0}</div>
                        </div>
                        <div style={{ backgroundColor: '#fdf2f8', padding: '8px', borderRadius: '6px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', color: '#db2777', textTransform: 'uppercase', marginBottom: '2px' }}>F/Up</div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#9d174d' }}>{staff.followup_leads || 0}</div>
                        </div>
                        <div style={{ backgroundColor: '#fffbeb', padding: '8px', borderRadius: '6px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', color: '#d97706', textTransform: 'uppercase', marginBottom: '2px' }}>Pend</div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#92400e' }}>{staff.pending_leads || 0}</div>
                        </div>
                        <div style={{ backgroundColor: '#f0fdf4', padding: '8px', borderRadius: '6px', textAlign: 'center' }}>
                          <div style={{ fontSize: '10px', color: '#16a34a', textTransform: 'uppercase', marginBottom: '2px' }}>Proc</div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#166534' }}>{staff.clients_in_processing || 0}</div>
                        </div>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leads?assigned_staff_id=${staff.id}&lead_source_type=direct`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/leads?assigned_staff_id=${staff.id}&lead_source_type=direct`);
                            }
                          }}
                          style={{
                            backgroundColor: '#eef2ff',
                            padding: '8px',
                            borderRadius: '6px',
                            textAlign: 'center',
                            cursor: 'pointer',
                          }}
                          title="Manually entered leads (not bulk import) assigned to this staff — click to open list"
                        >
                          <div style={{ fontSize: '10px', color: '#4338ca', textTransform: 'uppercase', marginBottom: '2px' }}>Direct</div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#3730a3' }}>{staff.direct_leads ?? 0}</div>
                        </div>
                      </div>
                      {(staff.unattended_leads || 0) > 0 && (
                        <div
                          style={{
                            marginTop: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 10px',
                            background: '#fef2f2',
                            borderRadius: '8px',
                            border: '1px solid #fecaca',
                            color: '#991b1b',
                            fontSize: '13px',
                            fontWeight: 600,
                          }}
                        >
                          <FiAlertTriangle size={18} style={{ flexShrink: 0 }} aria-hidden />
                          <span>
                            {staff.unattended_leads} unreachable follow-up{staff.unattended_leads !== 1 ? 's' : ''} — open dashboard to review
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )
        }

        <div style={{ display: 'grid', gridTemplateColumns: dashboardMode === 'main' ? '1fr 1fr' : '1fr', gap: '32px' }}>
          <div className="recent-activity">
            <h2>{dashboardMode === 'main' ? 'My Recent Activity' : 'Recent Activity'}</h2>
            {data.recentActivity && data.recentActivity.length > 0 ? (
              <div className="activity-list">
                {data.recentActivity.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">
                      {activity.type === 'comment' ? <FiActivity /> : <FiTrendingUp />}
                    </div>
                    <div className="activity-content">
                      <div className="activity-text">
                        {activity.type === 'comment' ? 'New comment' : 'Status updated'} on{' '}
                        <strong>{activity.lead_name}</strong>
                      </div>
                      <div className="activity-meta">
                        {activity.user_name && <span>by {activity.user_name} • </span>}
                        {new Date(activity.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-activity">No recent activity</p>
            )}
          </div>

          {dashboardMode === 'main' && data.recentLeads && (
            <div className="recent-leads-section" style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>My Recent Leads</h2>
              <div className="table-responsive">
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentLeads.slice(0, 10).map(lead => (
                      <tr key={lead.id} onClick={() => navigate(`/leads/${lead.id}`)} style={{ cursor: 'pointer' }}>
                        <td>{lead.name}</td>
                        <td>
                          <span className={`status-badge status-${lead.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                            {leadStatusLabel(lead.status)}
                          </span>
                        </td>
                        <td>{new Date(lead.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {renderAssignedLeadsModal()}
      </div >
    );
  }

  if (isStaffDetailView) {
    // Check if this is a processing team member (Sneha or Kripa)
    const isProcessingTeam = data.isProcessingTeam === true;
    const processingRole = data.processingRole; // 'sneha' or 'kripa'

    // If admin is viewing Sneha or Kripa, show their specialized dashboard instead of table view
    const isAdminViewingSneha = processingRole === 'sneha' && (user?.role === 'ADMIN' || user?.role === 'SALES_TEAM_HEAD');
    const isAdminViewingKripa = processingRole === 'kripa' && (user?.role === 'ADMIN' || user?.role === 'SALES_TEAM_HEAD');

    // For Sneha and Kripa, render their specialized dashboards for admins
    if (isAdminViewingSneha) {
      return (
        <div>
          <button className="dashboard-back" onClick={() => navigate('/')} style={{ marginBottom: '16px' }}>
            <FiArrowLeft /> Back to Dashboard
          </button>
          <SnehaDashboard viewingStaffId={Number(staffId)} />
        </div>
      );
    }

    if (isAdminViewingKripa) {
      return (
        <div>
          <button className="dashboard-back" onClick={() => navigate('/')} style={{ marginBottom: '16px' }}>
            <FiArrowLeft /> Back to Dashboard
          </button>
          <KripaDashboard viewingStaffId={Number(staffId)} />
        </div>
      );
    }

    const viewingOwnStaffDash =
      user?.id != null && staffId != null && Number(staffId) === Number(user.id);
    const backLabel =
      user?.role === 'ADMIN' && viewingOwnStaffDash
        ? 'Back to company dashboard'
        : 'Back to Dashboard';

    return (
      <div className="dashboard">
        <button className="dashboard-back" onClick={() => navigate('/')}>
          <FiArrowLeft /> {backLabel}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <h1 className="dashboard-title">
            {isProcessingTeam ? 'Processing Team Dashboard' : 'Staff Dashboard'} - {data.staff?.name || (staffId ? `Staff ID: ${staffId}` : 'Staff')}
          </h1>
          {isProcessingTeam && (
            <span style={{
              padding: '6px 16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#FFFFFF',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
            }}>
              🔧 Processing Team
            </span>
          )}
          {isReadOnly && (
            <span style={{
              padding: '4px 12px',
              background: '#FEF3C7',
              color: '#92400E',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 600
            }}>
              Read-Only Monitoring
            </span>
          )}
        </div>

        {isProcessingTeam ? (
          // Processing Team Dashboard View
          <>
            {/* Processing Metrics */}
            <div className="metrics-grid" style={{ marginBottom: '24px' }}>
              <div className="metric-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
                <div className="metric-icon" style={{ background: 'rgba(255,255,255,0.2)' }}>
                  <FiUsers style={{ color: '#fff' }} />
                </div>
                <div className="metric-content">
                  <div className="metric-value" style={{ color: '#fff' }}>{data.metrics?.totalClients || 0}</div>
                  <div className="metric-label" style={{ color: 'rgba(255,255,255,0.9)' }}>
                    {processingRole === 'sneha' ? 'Clients Assigned' : 'Clients In Processing'}
                  </div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon" style={{ background: '#FEE2E2' }}>
                  <FiClock style={{ color: '#DC2626' }} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{data.metrics?.paymentPending || 0}</div>
                  <div className="metric-label">Payment Pending</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon" style={{ background: '#D1FAE5' }}>
                  <FiCheck style={{ color: '#059669' }} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{data.metrics?.firstInstallmentCompleted || 0}</div>
                  <div className="metric-label">1st Installment Done</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon" style={{ background: '#DBEAFE' }}>
                  <FiActivity style={{ color: '#2563EB' }} />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{data.metrics?.pteFeePaid || 0}</div>
                  <div className="metric-label">PTE Fee Paid</div>
                </div>
              </div>
            </div>

            {/* Processing Clients List */}
            <div className="recent-leads-section">
              <h2>{processingRole === 'sneha' ? 'Clients Assigned to Sneha' : 'Clients In Processing (Kripa)'}</h2>
              {data.clientsList && data.clientsList.length > 0 ? (
                <div className="leads-list-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Fee Status</th>
                        <th>Amount Paid</th>
                        {processingRole === 'kripa' && <th>Processing Status</th>}
                        <th>Payment Due Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.clientsList || []).map((client) => (
                        <tr key={client.id}>
                          <td>{client.name}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiPhone style={{ fontSize: '14px', opacity: 0.6 }} />
                              {client.phone_country_code} {client.phone_number}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiMail style={{ fontSize: '14px', opacity: 0.6 }} />
                              {client.email || '-'}
                            </div>
                          </td>
                          <td>
                            <span className="status-badge" style={{
                              backgroundColor: client.fee_status === 'Payment Pending' ? '#FEE2E2' :
                                client.fee_status === '1st Installment Completed' ? '#D1FAE5' :
                                  client.fee_status === 'PTE Fee Paid' ? '#DBEAFE' : '#F3F4F6',
                              color: client.fee_status === 'Payment Pending' ? '#DC2626' :
                                client.fee_status === '1st Installment Completed' ? '#059669' :
                                  client.fee_status === 'PTE Fee Paid' ? '#2563EB' : '#6B7280',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 500
                            }}>
                              {client.fee_status || 'Not Set'}
                            </span>
                          </td>
                          <td>{client.amount_paid ? `د.إ ${client.amount_paid}` : '-'}</td>
                          {processingRole === 'kripa' && (
                            <td>
                              <span className="status-badge" style={{
                                backgroundColor: '#E0E7FF',
                                color: '#4338CA',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 500
                              }}>
                                {client.processing_status || 'In Progress'}
                              </span>
                            </td>
                          )}
                          <td>
                            {client.payment_due_date ? new Date(client.payment_due_date).toLocaleDateString() : '-'}
                          </td>
                          <td>
                            <button
                              className="action-button"
                              onClick={() => navigate(`/clients/${client.id}`)}
                              style={{ padding: '6px 12px', fontSize: '12px' }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#6B7280', padding: '40px' }}>
                  No clients {processingRole === 'sneha' ? 'assigned' : 'in processing'} yet
                </p>
              )}
            </div>
          </>
        ) : (
          // Regular Staff Dashboard View (NOT Processing Team)
          <>
            {renderStaffMetrics()}
            {renderUnattendedLeadsSection()}
            {renderStatusBreakdown()}
            {((data.recentLeads && data.recentLeads.length > 0) || (data.leadsList && data.leadsList.length > 0)) && (
              <div className="recent-leads-section" style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>Recent leads</h2>
                <div className="table-responsive">
                  <table className="leads-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.recentLeads || data.leadsList || [])
                        .slice()
                        .sort(
                          (a, b) =>
                            new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
                        )
                        .slice(0, 10)
                        .map((lead) => (
                          <tr key={lead.id} onClick={() => navigate(`/leads/${lead.id}`)} style={{ cursor: 'pointer' }}>
                            <td>{lead.name}</td>
                            <td>
                              <span className={`status-badge status-${lead.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                                {leadStatusLabel(lead.status)}
                              </span>
                            </td>
                            <td>{new Date(lead.updated_at || lead.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="recent-leads-section">
              <div
                style={{
                  padding: '16px 20px',
                  background: '#faf9f6',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '12px',
                  justifyContent: 'space-between',
                }}
              >
                <p style={{ margin: 0, fontSize: '14px', color: '#4b5563' }}>
                  <strong style={{ color: '#111827' }}>
                    {data.metrics?.totalLeads ?? data.leadsList?.length ?? 0}
                  </strong>{' '}
                  leads assigned to {viewingOwnStaffDash ? 'you' : data.staff?.name || 'this staff'} — open the full list for search and bulk actions.
                </p>
                <button
                  type="button"
                  className="btn-view-lead"
                  onClick={() =>
                    navigate(staffId != null ? `/leads?assigned_staff_id=${staffId}` : '/leads')
                  }
                  style={{ padding: '10px 18px', fontWeight: 600 }}
                >
                  <FiUsers style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  {viewingOwnStaffDash ? 'Open My Leads' : 'Open all leads for this staff'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ADMIN Dashboard
  return (
    <div className="dashboard">
      {isSreelakshmi && user?.id && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #fefce8 0%, #fff7ed 100%)',
            border: '1px solid #fcd34d',
            borderRadius: '12px',
          }}
        >
          <span style={{ fontWeight: 600, color: '#92400e' }}>Staff workspace</span>
          <button
            type="button"
            onClick={() => navigate(`/dashboard/staff/${user.id}`)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              background: 'linear-gradient(180deg, #D4AF37, #B8860B)',
              color: '#fff',
            }}
          >
            Open my work dashboard
          </button>
          <span style={{ fontSize: '13px', color: '#78716c' }}>
            Assigned leads, follow-ups, and clients — same view admins use to monitor you
          </span>
        </div>
      )}
      <div className="dashboard-header">
        <h1 className="dashboard-title">
          {data.role === 'ADMIN' ? 'Company Dashboard' : 'Team Dashboard'}
        </h1>
        {data.role === 'SALES_TEAM_HEAD' && (
          <p style={{
            color: '#6b7280',
            fontSize: '14px',
            marginTop: '8px',
            fontStyle: 'italic'
          }}>
            Monitor and manage your team members' performance
          </p>
        )}
      </div>

      {renderAssignedByMe()}

      {/* Metrics Overview */}
      <div className="dashboard-section">
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-icon" style={{ background: '#FFF4D6' }}>
              <FiUsers style={{ color: '#D4AF37' }} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{data.metrics.totalLeads}</div>
              <div className="metric-label">Total Leads</div>
            </div>
          </div>
          {data.metrics.totalClients !== undefined && (
            <div className="metric-card">
              <div className="metric-icon" style={{ background: '#D1FAE5' }}>
                <FiCheck style={{ color: '#10B981' }} />
              </div>
              <div className="metric-content">
                <div className="metric-value">{data.metrics.totalClients}</div>
                <div className="metric-label">Total Clients</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="dashboard-section">
        <div className="status-breakdown">
          <h2>Leads by Status</h2>
          <div className="status-grid">
            {Object.entries(data.metrics?.leadsByStatus || {}).map(([status, count]) => (
              <div key={status} className="status-item">
                <span className="status-label">{leadStatusLabel(status)}</span>
                <span className="status-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Client Metrics */}
      {data.metrics.clientsByStatus && (
        <div className="dashboard-section">
          <div className="status-breakdown">
            <h2>Clients Overview</h2>
            <div className="status-grid">
              {Object.entries(data.metrics?.clientsByStatus || {}).map(([status, count]) => (
                <div key={status} className="status-item">
                  <span className="status-label">{leadStatusLabel(status)}</span>
                  <span className="status-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recent Leads */}
      <div className="dashboard-section">
        <div className="recent-leads-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2>Recent Leads</h2>
            {selectedLeads.length > 0 && (
              <button
                onClick={handleBulkDelete}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 500,
                  fontSize: '13px'
                }}
              >
                <FiTrash2 /> Delete Selected ({selectedLeads.length})
              </button>
            )}
          </div>
          {data.recentLeads && data.recentLeads.length > 0 ? (
            <div className="leads-list-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40px', paddingLeft: '16px' }}>
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={data.recentLeads.length > 0 && selectedLeads.length === data.recentLeads.length}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Assigned To</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recentLeads || []).map((lead) => (
                    <tr key={lead.id} style={selectedLeads.includes(lead.id) ? { backgroundColor: '#f0f9ff' } : {}}>
                      <td style={{ paddingLeft: '16px' }}>
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleSelect(lead.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td>{lead.name}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <FiPhone style={{ fontSize: '14px', opacity: 0.6 }} />
                          {lead.phone_country_code && lead.phone_number ? (
                            <span>{lead.phone_country_code} {lead.phone_number}</span>
                          ) : (
                            lead.phone_number || '-'
                          )}
                        </div>
                      </td>
                      <td>
                        {lead.email ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiMail style={{ fontSize: '14px', opacity: 0.6 }} />
                            {lead.email}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: `${getStatusColor(lead.status)}20`,
                            color: getStatusColor(lead.status),
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500,
                          }}
                        >
                          {leadStatusLabel(lead.status)}
                        </span>
                      </td>
                      <td>
                        {lead.priority ? (
                          <span
                            className="priority-badge"
                            style={{
                              backgroundColor: `${getPriorityColor(lead.priority)}20`,
                              color: getPriorityColor(lead.priority),
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 500,
                            }}
                          >
                            {formatPriority(lead.priority)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            fontWeight: lead.assigned_staff_name ? 600 : 400,
                            color: lead.assigned_staff_name ? '#8B6914' : '#9ca3af',
                            fontSize: '14px',
                          }}
                        >
                          {lead.assigned_staff_name || 'Unassigned'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-view-lead"
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          style={{ marginRight: '8px' }}
                        >
                          <FiEdit2 /> View
                        </button>
                        <button
                          className="btn-delete-lead"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(lead.id, lead.name);
                          }}
                          style={{
                            background: '#fee2e2',
                            color: '#ef4444',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '13px',
                            fontWeight: 500,
                            transition: 'all 0.2s'
                          }}
                        >
                          <FiTrash2 /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No leads found</p>
          )}
          <div style={{ marginTop: '16px' }}>
            <button
              className="btn-view-all-leads"
              onClick={() => navigate('/leads')}
            >
              View All Leads
            </button>
          </div>
        </div>
      </div>

      {/* Recent Clients */}
      {data.recentClients && data.recentClients.length > 0 && (
        <div className="dashboard-section">
          <div className="recent-leads-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2>Recent Clients</h2>
              {selectedClients.length > 0 && (
                <button
                  onClick={handleBulkDeleteClients}
                  style={{
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: 500,
                    fontSize: '13px'
                  }}
                >
                  <FiTrash2 /> Delete Selected ({selectedClients.length})
                </button>
              )}
            </div>
            <div className="leads-list-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40px', paddingLeft: '16px' }}>
                      <input
                        type="checkbox"
                        onChange={handleSelectAllClients}
                        checked={data.recentClients && data.recentClients.length > 0 && selectedClients.length === data.recentClients.length}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Fee Status</th>
                    <th>Assigned To</th>
                    <th>Processing</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recentClients || []).map((client) => (
                    <tr key={client.id} style={selectedClients.includes(client.id) ? { backgroundColor: '#f0f9ff' } : {}}>
                      <td style={{ paddingLeft: '16px' }}>
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(client.id)}
                          onChange={() => handleSelectClient(client.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td>{client.name}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <FiPhone style={{ fontSize: '14px', opacity: 0.6 }} />
                          {client.phone_country_code && client.phone_number ? (
                            <span>{client.phone_country_code} {client.phone_number}</span>
                          ) : (
                            client.phone_number || '-'
                          )}
                        </div>
                      </td>
                      <td>
                        {client.email ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiMail style={{ fontSize: '14px', opacity: 0.6 }} />
                            {client.email}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {client.fee_status ? (
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: client.fee_status === 'Payment Pending' ? '#FEE2E220' : client.fee_status === '1st Installment Completed' ? '#D1FAE520' : '#DBEAFE20',
                              color: client.fee_status === 'Payment Pending' ? '#DC2626' : client.fee_status === '1st Installment Completed' ? '#10B981' : '#2563EB',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 500,
                            }}
                          >
                            {client.fee_status}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            fontWeight: client.assigned_staff_name ? 600 : 400,
                            color: client.assigned_staff_name ? '#8B6914' : '#9ca3af',
                            fontSize: '14px',
                          }}
                        >
                          {client.assigned_staff_name || 'Unassigned'}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            fontWeight: client.processing_staff_name ? 600 : 400,
                            color: client.processing_staff_name ? '#059669' : '#9ca3af',
                            fontSize: '14px',
                          }}
                        >
                          {client.processing_staff_name || 'Not in Processing'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-view-lead"
                          onClick={() => navigate(`/clients/${client.id}`)}
                        >
                          <FiEdit2 /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: '16px' }}>
              <button
                className="btn-view-all-leads"
                onClick={() => navigate('/clients')}
              >
                View All Clients
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Staff/Team Performance */}
      {(data.role === 'ADMIN' || data.role === 'SALES_TEAM_HEAD') && (
        <div className="dashboard-section">
          <div className="staff-performance">
            <h2>{data.role === 'ADMIN' ? 'Staff List' : 'My Team Members'}</h2>
            {data.role === 'SALES_TEAM_HEAD' && (
              <p style={{
                color: '#6b7280',
                fontSize: '13px',
                marginBottom: '16px'
              }}>
                Click on any team member to view their detailed dashboard and monitor their performance
              </p>
            )}
            {data && data.staffPerformance && Array.isArray(data.staffPerformance) && data.staffPerformance.length > 0 ? (
              <div className="performance-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Total Leads</th>
                      <th>Direct leads</th>
                      <th>In Processing</th>
                      <th>Unreachable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.staffPerformance || []).map((staff) => {
                      // Check if staff is Sneha or Kripa (Processing Team)
                      const isSneha = isProcessingTeamSnehaUser(staff);
                      const isKripa = staff.name === 'Kripa' || staff.name === 'KRIPA' || staff.email === 'kripa@toniosenora.com';
                      const isProcessingTeam = isSneha || isKripa;
                      // Check if this is the sales team head themselves
                      const isTeamHead = staff.id === user?.id;

                      return (
                        <tr
                          key={staff.id}
                          className="staff-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate(`/dashboard/staff/${staff.id}`)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              navigate(`/dashboard/staff/${staff.id}`);
                            }
                          }}
                          style={{
                            ...(isProcessingTeam ? {
                              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                              borderLeft: '4px solid #667eea'
                            } : {}),
                            ...(isTeamHead ? {
                              background: '#FFF4D6',
                              borderLeft: '4px solid #D4AF37',
                              fontWeight: 600
                            } : {}),
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!isProcessingTeam && !isTeamHead) {
                              e.currentTarget.style.backgroundColor = '#F5F1E8';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isProcessingTeam && !isTeamHead) {
                              e.currentTarget.style.backgroundColor = '';
                            }
                          }}
                        >
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="staff-link" style={{
                                color: isTeamHead ? '#8B6914' : 'inherit',
                                fontWeight: isTeamHead ? 600 : 'normal'
                              }}>
                                {staff.name}
                                {isTeamHead && ' (You)'}
                              </span>
                              {isProcessingTeam && (
                                <span style={{
                                  padding: '2px 8px',
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color: '#FFFFFF',
                                  borderRadius: '10px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  boxShadow: '0 1px 3px rgba(102, 126, 234, 0.3)'
                                }}>
                                  🔧 Processing
                                </span>
                              )}
                              {data.role === 'SALES_TEAM_HEAD' && !isTeamHead && (
                                <span style={{
                                  padding: '2px 8px',
                                  background: '#E0E7FF',
                                  color: '#4338CA',
                                  borderRadius: '10px',
                                  fontSize: '10px',
                                  fontWeight: 500
                                }}>
                                  Team Member
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{staff.total_leads}</td>
                          <td>{staff.direct_leads ?? 0}</td>
                          <td>{staff.clients_in_processing || 0}</td>
                          <td>
                            {(staff.unattended_leads || 0) > 0 ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#b91c1c', fontWeight: 600 }}>
                                <FiAlertTriangle size={16} aria-hidden />
                                {staff.unattended_leads}
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>0</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                <p>No staff performance data available</p>
                {data && data.role === 'SALES_TEAM_HEAD' && (
                  <div style={{ fontSize: '12px', marginTop: '8px', fontStyle: 'italic', textAlign: 'left', background: '#f5f5f5', padding: '10px', borderRadius: '4px', maxWidth: '600px', margin: '10px auto' }}>
                    <p><strong>Debug Information:</strong></p>
                    <p>staffPerformance exists: {data.staffPerformance ? 'Yes' : 'No'}</p>
                    <p>staffPerformance type: {data.staffPerformance ? typeof data.staffPerformance : 'N/A'}</p>
                    <p>staffPerformance isArray: {data.staffPerformance ? Array.isArray(data.staffPerformance) ? 'Yes' : 'No' : 'N/A'}</p>
                    <p>staffPerformance length: {data.staffPerformance && Array.isArray(data.staffPerformance) ? data.staffPerformance.length : 'N/A'}</p>
                    {data.staffPerformance && Array.isArray(data.staffPerformance) && data.staffPerformance.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <p><strong>Staff Members Found ({data.staffPerformance.length}):</strong></p>
                        <ul style={{ marginLeft: '20px', textAlign: 'left' }}>
                          {data.staffPerformance.map((staff, idx) => (
                            <li key={idx}>{staff.name} (ID: {staff.id}, Leads: {staff.total_leads}, Direct: {staff.direct_leads ?? 0})</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {renderAssignedLeadsModal()}
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = {
    'New': '#D4AF37',
    'Follow-up': '#C9A961',
    'Prospect': '#B8860B',
    'Pending Lead': '#8B6914',
    'Not Responding': '#b45309',
    'Not Attended': '#ea580c',
    'Not Eligible': '#b91c1c',
    'Not Interested': '#4b5563',
    'Registration Completed': '#1d4ed8',
    'Closed / Rejected': '#A0826D',
  };
  return colors[status] || '#8B6914';
};

const getPriorityColor = (priority) => {
  const colors = {
    'hot': '#ef4444',
    'warm': '#f59e0b',
    'cold': '#3b82f6',
    'not interested': '#6b7280',
    'not eligible': '#dc2626',
  };
  return colors[priority] || '#6b7280';
};

const formatPriority = (priority) => {
  if (!priority) return '-';
  return priority.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

export default Dashboard;
